import type { Breadcrumb } from '@sentry/vue';

export const REDACTED_SIGNED_URL = '[redacted signed URL]';

// Context: a proof download hands the browser a pre-signed S3 URL that grants the document to
// whoever holds it for about a minute, so it must never reach Sentry (DL-151). The signature and
// credential parameters are what make a URL usable, which is why they are the marker.
const SIGNED_URL_MARKER = /X-Amz-(?:Signature|Credential)=/i;

// This runs synchronously on every breadcrumb, so it must stay linear in the string's length. One
// pattern hunting for the marker from every start position backtracks quadratically over a long
// run without one (a logged data URL would freeze the tab for seconds), so each run of URL-safe
// characters is matched whole and then tested for the marker.
const URL_LIKE_RUN = /[^\s"'<>]+/g;

// The two routes whose response body carries a signed URL (PE-1011's and PE-1003's). The route
// itself is harmless, but its XHR breadcrumb is the trail that leads to the URL, so it is dropped.
const DOWNLOAD_ROUTES = [
  /\/payments\/[^/?#]+\/proof\/download(?:[?#]|$)/i,
  /\/payment-proof-submissions\/[^/?#]+\/document\/download(?:[?#]|$)/i,
];

const REQUEST_CATEGORIES = new Set(['xhr', 'fetch']);

// Console breadcrumbs carry their arguments as arrays and objects, so the walk goes a few levels
// down. Deeper than that is an object nobody logs on purpose.
const MAX_DEPTH = 4;

const redactString = (value: string): string =>
  SIGNED_URL_MARKER.test(value)
    ? value.replaceAll(URL_LIKE_RUN, (run) =>
        SIGNED_URL_MARKER.test(run) ? REDACTED_SIGNED_URL : run,
      )
    : value;

const isPlainObject = (value: object): value is Record<string, unknown> => {
  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

// Only arrays and plain objects are rebuilt: rebuilding an Error or a Date from its enumerable own
// properties would hand Sentry an empty object in its place.
const redactValue = (value: unknown, depth: number): unknown => {
  if (typeof value === 'string') {
    return redactString(value);
  }
  if (depth >= MAX_DEPTH || value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactValue(item, depth + 1));
  }
  if (!isPlainObject(value)) {
    return value;
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, redactValue(item, depth + 1)]),
  );
};

const isDownloadRequest = (breadcrumb: Breadcrumb): boolean => {
  const url = breadcrumb.data?.url;
  return (
    REQUEST_CATEGORIES.has(breadcrumb.category ?? '') &&
    typeof url === 'string' &&
    DOWNLOAD_ROUTES.some((route) => route.test(url))
  );
};

/**
 * Sentry's `beforeBreadcrumb`: drops both proof-download routes' request breadcrumbs and redacts
 * any signed storage URL from every other breadcrumb's message and data.
 */
export const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null => {
  if (isDownloadRequest(breadcrumb)) {
    return null;
  }

  return {
    ...breadcrumb,
    ...(breadcrumb.message === undefined
      ? {}
      : { message: redactValue(breadcrumb.message, 0) as string }),
    ...(breadcrumb.data === undefined
      ? {}
      : { data: redactValue(breadcrumb.data, 0) as Breadcrumb['data'] }),
  };
};
